import { useT } from "@transifex/react";
import type { SchoolClass, StudentTestContentWrapper } from "p6m-p6u";
import { ClassActivityList } from "./ClassActivityList";
import { ClassActivityPopups } from "./ClassActivityPopups";
import type { ClassActivity } from "./ClassActivityCardsOverview.types";
import type { useTestOperations } from "../../hooks/useTestOperations";
import type { useVocabTrainingOperations } from "../../hooks/useVocabTrainingOperations";

interface ActivityListWithPopupsProps {
    activities: ClassActivity[];
    schoolClasses: SchoolClass[];
    studentTestContentWrapper: StudentTestContentWrapper;
    classFilter: string;
    languageFilter: string;
    testOps: ReturnType<typeof useTestOperations>;
    vocabTrainingOps: ReturnType<typeof useVocabTrainingOperations>;
    userTestsLoadError: boolean;
}

export const ActivityListWithPopups = (props: ActivityListWithPopupsProps) => {
    const t = useT();

    const t_vocabularyFrom = (source: string) => t("Vocabulary from {source}", { _tags: "AssistantOverview", source });
    const t_oclock = t("o'clock", { _tags: "AssistantOverview" });
    const t_lastModifiedOn = t("Last modified on:", { _tags: "AssistantOverview" });
    const t_createdOn = t("Created on:", { _tags: "AssistantOverview" });
    const t_noFilterResults = t("No results matching the selected filter criteria", {
        _tags: "ClassActivity, ActivityFilterBar",
    });
    const t_vocabTrainingsLoadError = t("Vocab Trainings couldn't be loaded. Please try again later.", {
        _tags: "ClassActivity, ActivityList",
    });
    const t_userTestsLoadError = t("Tests couldn't be loaded. Please try again later.", {
        _tags: "ClassActivity, ActivityList",
    });
    const t_activitiesLoadError = t("Activities couldn't be loaded. Please try again later.", {
        _tags: "ClassActivity, ActivityList",
    });

    const t_deleteVocabTraining = t("Delete assigned vocabulary", { _tags: "AssistantOverview" });
    const t_deleteVocabTrainingConfirmation = t("Do you really want to delete <b>your assigned vocabulary</b>?", {
        _tags: "AssistantOverview",
    });
    const t_deleteTask = t("Delete Task", { _tags: "ClassActivity, ClassActivityPopup" });
    const t_deleteTaskConfirmation = (taskName: string) =>
        t("Do you really want to delete <b>{taskName}</b>?", {
            _tags: "ClassActivity, ClassActivityPopup",
            taskName,
        });
    const t_deleteConfirmationNote = t("All previous answers and evaluations will also be deleted.", {
        _tags: "ClassActivity, ClassActivityPopup",
    });
    const t_duplicateTask = t("Duplicate Task", { _tags: "ClassActivity, ClassActivityPopup" });
    const t_duplicateVocabTraining = t("Duplicate vocabulary training", {
        _tags: "ClassActivity, ClassActivityPopup",
    });
    const t_taskName = t("Task name", { _tags: "ClassActivity, ClassActivityPopup" });
    const t_vocabTrainingName = t("Vocabulary training name", { _tags: "ClassActivity, ClassActivityPopup" });
    const t_sr_duplicate = t("Duplicate", { _tags: "ClassActivity, ScreenReaderOnly" });
    const t_copyOf = (name: string) => t("Copy of {name}", { _tags: "ClassActivity, ClassActivityPopup", name });
    const t_assignCopyToClass = (schoolClassName: string) =>
        t("Assign copy to class <b>{className}</b>", {
            _tags: "ClassActivity, ClassActivityPopup",
            className: schoolClassName,
        });
    const t_assignCopyToSameClass = t("Assign copy to the same class", { _tags: "ClassActivity, ClassActivityPopup" });

    return (
        <>
            <ClassActivityList
                activities={props.activities}
                schoolClasses={props.schoolClasses}
                studentTestContentWrapper={props.studentTestContentWrapper}
                classFilter={props.classFilter}
                languageFilter={props.languageFilter}
                testOps={props.testOps}
                vocabTrainingOps={props.vocabTrainingOps}
                onShareVocabTraining={props.vocabTrainingOps.handleShareButtonClick}
                vocabTrainingsLoadError={props.vocabTrainingOps.vocabTrainingsLoadError}
                userTestsLoadError={props.userTestsLoadError}
                translations={{
                    vocabularyFrom: t_vocabularyFrom,
                    oclock: t_oclock,
                    lastModifiedOn: t_lastModifiedOn,
                    createdOn: t_createdOn,
                    noFilterResults: t_noFilterResults,
                    vocabTrainingsLoadError: t_vocabTrainingsLoadError,
                    userTestsLoadError: t_userTestsLoadError,
                    activitiesLoadError: t_activitiesLoadError,
                }}
            />

            <ClassActivityPopups
                testOps={props.testOps}
                vocabTrainingOps={props.vocabTrainingOps}
                schoolClasses={props.schoolClasses}
                studentTestContentWrapper={props.studentTestContentWrapper}
                translations={{
                    deleteVocabTraining: t_deleteVocabTraining,
                    deleteVocabTrainingConfirmation: t_deleteVocabTrainingConfirmation,
                    deleteTask: t_deleteTask,
                    deleteTaskConfirmation: t_deleteTaskConfirmation,
                    deleteConfirmationNote: t_deleteConfirmationNote,
                    duplicateTask: t_duplicateTask,
                    duplicateVocabTraining: t_duplicateVocabTraining,
                    duplicate: t_sr_duplicate,
                    taskName: t_taskName,
                    vocabTrainingName: t_vocabTrainingName,
                    copyOf: t_copyOf,
                    assignCopyToClass: t_assignCopyToClass,
                    assignCopyToSameClass: t_assignCopyToSameClass,
                }}
            />
        </>
    );
};
